import { actionProps } from "@/lib/controlAnalytics";
import { DownloadIcon } from "@/components/icons";
import type { TailoredResumeLink } from "@/lib/api/resumeService";
import { describeError } from "@/lib/api/client";
import { formatBytes } from "@/lib/format";
import { useOpenTailored, useResumeVersion, useTailoredChanges } from "@/lib/hooks/resume";
import {
    changeRows,
    dayOf,
    sentPhrase,
    sentResume,
    stamp,
    type ChangeRow,
    type FitView,
    type Sent,
    type WrittenResume,
} from "@/lib/posting";
import ResumeSheet from "./ResumeSheet";
import { GroupLabel, KeywordChips, PdfTile } from "./parts";

export type SentView = "sent" | "changes";

const KIND_TONE: Record<ChangeRow["tone"], string> = {
    applied: "bg-applied text-applied-fg",
    raised: "bg-card-raised text-fg-muted",
    queued: "bg-queued text-queued-fg",
};

function Changes({ resumeId }: { resumeId: string }) {
    const changes = useTailoredChanges(resumeId);
    const version = useResumeVersion(changes.data?.baseline_version);
    if (changes.isPending)
        return <p className="m-0 text-ui text-fg-muted">Reading what changed…</p>;
    if (changes.isError)
        return (
            <p className="m-0 text-ui text-fg-muted">
                What the tailor changed is not on record for this resume.{" "}
                {describeError(changes.error)}
            </p>
        );
    const rows = changeRows(changes.data.changes);
    const against = version.data
        ? `Against your resume as saved on ${dayOf(version.data.saved_at)}, version ${changes.data.baseline_version}.`
        : `Against version ${changes.data.baseline_version} of your resume.`;
    return (
        <div className="flex flex-col gap-2.5">
            <span className="text-ui text-fg-muted [text-wrap:pretty]">
                {against} The tailor reorders and rewords; it may not add a skill, employer, title,
                date or number, and the guard checked every one before the file was kept.
            </span>
            {rows.map((row, index) => (
                <div
                    key={index}
                    className="grid grid-cols-[90px_minmax(0,1fr)] gap-3 rounded-control border border-line bg-card px-3.5 py-3"
                >
                    <span
                        className={`self-start whitespace-nowrap justify-self-start rounded-full px-[9px] py-[3px] text-small font-semibold ${KIND_TONE[row.tone]}`}
                    >
                        {row.kind}
                    </span>
                    <div className="flex min-w-0 flex-col gap-1.5">
                        <span className="font-semibold">{row.what}</span>
                        {row.before && (
                            <span className="text-ui text-fg-muted line-through decoration-fg-faint">
                                {row.before}
                            </span>
                        )}
                        {row.after && (
                            <span className="rounded-lg bg-applied px-2.5 py-1.5 text-ui text-fg">
                                {row.after}
                            </span>
                        )}
                        {row.note && (
                            <span className="text-ui text-fg-muted [overflow-wrap:anywhere]">
                                {row.note}
                            </span>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

function AsSent({ resumeId }: { resumeId: string }) {
    const changes = useTailoredChanges(resumeId);
    const version = useResumeVersion(changes.data?.baseline_version);
    const openPdf = useOpenTailored();
    const resume =
        changes.data && version.data ? sentResume(version.data.resume, changes.data.changes) : null;
    if (changes.isPending || (changes.isSuccess && version.isPending)) {
        return <p className="m-0 text-ui text-fg-muted">Setting the sheet…</p>;
    }
    if (resume && version.data) return <ResumeSheet resume={resume} format={version.data.format} />;
    return (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-control bg-card-raised px-4 py-3.5 text-ui text-fg-muted">
            <span className="max-w-[60ch] [text-wrap:pretty]">
                This sheet cannot be set again exactly from your resume and the tailor's changes.
                The PDF is the record of what went.
            </span>
            <button
                {...actionProps("tailored_pdf_open_clicked", {
                    source: "SentCard.AsSent",
                    resume_id: resumeId,
                })}
                type="button"
                onClick={() => openPdf.mutate(resumeId)}
                className="inline-flex h-8 max-[760px]:min-h-11 items-center gap-[5px] rounded-[9px] border border-line-strong bg-card px-3 text-small font-semibold text-fg"
            >
                <DownloadIcon className="size-[13px]" />
                Open the PDF
            </button>
        </div>
    );
}

/**
 * What was sent: the resume that went as the headline, its hash, who
 * sent it and when, the sheet itself or what the tailor changed, and the
 * keywords covered and missed.
 */
export default function SentCard({
    sent,
    written,
    link,
    modelName,
    fit,
    view,
    onView,
}: {
    sent: Sent;
    written: WrittenResume | undefined;
    link: TailoredResumeLink | undefined;
    modelName: (model: string) => string;
    fit: FitView | null;
    view: SentView;
    onView: (view: SentView) => void;
}) {
    const resumeId = sent.resumeId;
    const tailoredBy = written
        ? `tailored by ${written.tailor === "keyword" ? "the keyword tailor" : written.model ? modelName(written.model) : "the tailor"} on ${stamp(written.at)}`
        : null;
    const toggle = (key: SentView) =>
        `h-[30px] max-[760px]:min-h-11 rounded-lg px-3 text-small ${
            view === key ? "bg-fg font-semibold text-on-fg" : "font-medium text-fg-muted"
        }`;

    return (
        <section
            aria-labelledby="sent-title"
            className="flex flex-col gap-3.5 rounded-card border border-line bg-card p-5 shadow-card"
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                    <PdfTile size="lg" />
                    <div className="flex min-w-0 flex-col gap-[3px]">
                        <h2
                            id="sent-title"
                            className="m-0 font-display text-card-title font-bold tracking-[-0.01em]"
                        >
                            What was sent
                        </h2>
                        <span className="text-ui text-fg-muted">
                            {resumeId ? (
                                <>
                                    <span className="font-mono text-fg">Tailored resume</span>
                                    {[
                                        link ? formatBytes(link.byte_size) : null,
                                        tailoredBy,
                                        `sent ${sentPhrase(sent)}`,
                                    ]
                                        .filter(Boolean)
                                        .map((part) => ` · ${part}`)
                                        .join("")}
                                </>
                            ) : (
                                `Your saved resume, sent ${sentPhrase(sent)}. No tailored resume went with it.`
                            )}
                        </span>
                        {link && (
                            <span className="font-mono text-small text-fg-faint">
                                sha256 {link.sha256.slice(0, 8)}…{link.sha256.slice(-4)}
                            </span>
                        )}
                    </div>
                </div>
                {resumeId && (
                    <div
                        role="group"
                        aria-label="View"
                        className="inline-flex rounded-[11px] border border-line bg-card-raised p-[3px]"
                    >
                        <button
                            {...actionProps("sent_resume_view_selected", {
                                source: "SentCard",
                                option: "sent",
                            })}
                            type="button"
                            aria-pressed={view === "sent"}
                            onClick={() => onView("sent")}
                            className={toggle("sent")}
                        >
                            As sent
                        </button>
                        <button
                            {...actionProps("sent_resume_view_selected", {
                                source: "SentCard",
                                option: "changes",
                            })}
                            type="button"
                            aria-pressed={view === "changes"}
                            onClick={() => onView("changes")}
                            className={toggle("changes")}
                        >
                            What changed
                        </button>
                    </div>
                )}
            </div>

            {resumeId &&
                (view === "sent" ? (
                    <AsSent resumeId={resumeId} />
                ) : (
                    <Changes resumeId={resumeId} />
                ))}

            {fit && fit.listed && fit.asked > 0 && (
                <div className="flex flex-wrap gap-4 border-t border-line-soft pt-3">
                    <div className="flex flex-[1_1_240px] flex-col gap-1.5">
                        <GroupLabel>
                            Keywords covered at match · {fit.covered} of {fit.asked}
                        </GroupLabel>
                        <KeywordChips terms={fit.coveredTerms} kind="covered" />
                    </div>
                    <div className="flex flex-[1_1_200px] flex-col gap-1.5">
                        <GroupLabel>Not on the resume</GroupLabel>
                        <KeywordChips terms={fit.missing} kind="missing" />
                    </div>
                </div>
            )}
        </section>
    );
}
